import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel, TextField } from "../admin/fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export const useEresDbs = () => useQuery({ queryKey: ["eres-dbs"], queryFn: () => api.get("/platform/eres-dbs").then((r) => r.data) });

const EMPTY = { name: "", api_url: "", requestor_id: "", agent_code: "" };

const DbDialog = ({ edit, onClose }) => {
  const [form, setForm] = useState(edit?.id ? edit : EMPTY);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { name: form.name, api_url: form.api_url, requestor_id: form.requestor_id, agent_code: form.agent_code };
      await (edit?.id ? api.put(`/platform/eres-dbs/${edit.id}`, body) : api.post("/platform/eres-dbs", body));
      qc.invalidateQueries({ queryKey: ["eres-dbs"] });
      qc.invalidateQueries({ queryKey: ["platform-sites"] });
      toast.success("eRes database saved");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-sans" data-testid="eres-db-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Edit eRes database" : "Add eRes database"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <TextField label="Name" value={form.name} onChange={set("name")} placeholder="e.g. H.T.I. shared eRes" testid="eres-db-name" />
          <TextField label="eRes API address" value={form.api_url} onChange={set("api_url")} placeholder="https://..." testid="eres-db-url" />
          <TextField label="Requestor ID (ERSP)" value={form.requestor_id} onChange={set("requestor_id")} testid="eres-db-requestor" />
          <TextField label="Agent code (optional)" value={form.agent_code} onChange={set("agent_code")} testid="eres-db-agent" />
        </div>
        <button className="btn-primary mt-2" onClick={save} disabled={busy} data-testid="eres-db-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const EresDbPanel = () => {
  const { data } = useEresDbs();
  const [edit, setEdit] = useState(null);
  const qc = useQueryClient();
  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name}?`)) return;
    try {
      await api.delete(`/platform/eres-dbs/${d.id}`);
      qc.invalidateQueries({ queryKey: ["eres-dbs"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Panel title="eRes databases" hint="The shared H.T.I. database and any hotel's own eRes database."
      action={<button className="btn-outline !py-2" onClick={() => setEdit({})} data-testid="eres-db-add"><Plus className="h-4 w-4" />Add database</button>}>
      <div className="divide-y divide-ink/10" data-testid="eres-db-list">
        {(data || []).map((d) => (
          <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`eres-db-${d.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              <Database className="h-5 w-5 shrink-0 text-brand" />
              <div className="min-w-0"><p className="font-medium">{d.name}</p><p className="truncate font-mono text-xs text-ink/50">{d.api_url} · {d.requestor_id}</p></div>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => setEdit(d)} data-testid={`eres-db-edit-${d.id}`}><Pencil className="h-4 w-4" /></button>
              <button className="btn-ghost !text-red-600" onClick={() => remove(d)} data-testid={`eres-db-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {data?.length === 0 && <p className="py-3 text-sm text-ink/55">No eRes databases yet.</p>}
      </div>
      {edit && <DbDialog edit={edit} onClose={() => setEdit(null)} />}
    </Panel>
  );
};
